import React from 'react';
import { Link } from 'react-router-dom';
import LogoHead from '../logos/LogoHead';

// Simple inline SVG icons for socials (lucide doesn't have YouTube/Instagram)
const YoutubeIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);
const InstagramIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
);

const LINKS = [
    {
        heading: 'Product',
        items: [
            { label: 'Features', href: '#features' },
            { label: 'Pricing', href: '#pricing' },
            { label: 'Sign Up', href: '/signup' },
            { label: 'Log In', href: '/login' },
        ],
    },
    {
        heading: 'Company',
        items: [
            { label: 'Eternium', href: 'https://eternium.ai' },
            { label: 'YouTube', href: 'https://www.youtube.com/@TyrinBarneyAI' },
            { label: 'Instagram', href: 'https://www.instagram.com/tyrin.barney' },
        ],
    },
    {
        heading: 'Legal',
        items: [
            { label: 'Privacy Policy', href: '/privacy' },
            { label: 'Terms of Service', href: '/terms' },
        ],
    },
];

const SOCIALS = [
    { icon: YoutubeIcon, href: 'https://www.youtube.com/@TyrinBarneyAI', label: 'YouTube' },
    { icon: InstagramIcon, href: 'https://www.instagram.com/tyrin.barney', label: 'Instagram' },
];

const LandingFooter = () => {
    return (
        <footer className="relative border-t border-white/6 py-16 px-6">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
                    {/* Brand column */}
                    <div className="col-span-2">
                        <Link to="/" className="inline-flex items-center gap-2.5 mb-4 group">
                            <div className="w-9 h-9 rounded-lg overflow-hidden shadow-[0_0_12px_rgba(24,181,240,0.3)]">
                                <LogoHead className="w-full h-full" />
                            </div>
                            <span className="font-heading font-bold text-xl tracking-tight text-white">Helix</span>
                        </Link>
                        <p className="text-sm text-[var(--text-secondary)] max-w-xs leading-relaxed">
                            Your unhinged AI creative companion. Built by Eternium for people who make things nobody asked for.
                        </p>

                        {/* Socials */}
                        <div className="flex items-center gap-3 mt-6">
                            {SOCIALS.map(({ icon: Icon, href, label }) => (
                                <a
                                    key={label}
                                    href={href}
                                    aria-label={label}
                                    className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[var(--text-secondary)] hover:text-white hover:border-white/25 transition-all"
                                >
                                    <Icon size={16} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Link columns */}
                    {LINKS.map(({ heading, items }) => (
                        <div key={heading}>
                            <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-4">{heading}</p>
                            <ul className="space-y-2.5">
                                {items.map(({ label, href }) => (
                                    <li key={label}>
                                        {/* Internal SPA routes (no hash) use <Link>, everything else <a> */}
                                        {href.startsWith('/') ? (
                                            <Link
                                                to={href}
                                                className="text-sm text-[var(--text-secondary)] hover:text-white transition-colors"
                                            >
                                                {label}
                                            </Link>
                                        ) : (
                                            <a
                                                href={href}
                                                className="text-sm text-[var(--text-secondary)] hover:text-white transition-colors"
                                            >
                                                {label}
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom bar */}
                <div className="border-t border-white/6 pt-8 flex flex-col md:flex-row items-center justify-between gap-3">
                    <p className="text-xs text-[var(--text-secondary)]">
                        © {new Date().getFullYear()} Eternium LLC. All rights reserved.
                    </p>
                    <p className="text-xs text-white/20 uppercase tracking-widest">
                        Helix · v0.3.0
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default LandingFooter;
